import { ALL_BIZY3_STATES, type Bizy3State } from './bizy3-poses.generated';

/** Legacy state names remain public while call sites migrate to directional Bizy3 poses. */
export const LEGACY_BIZY_STATES = ['thinking', 'disappointed', 'buzzing'] as const;

export const ALL_BIZY_STATES = [...ALL_BIZY3_STATES, ...LEGACY_BIZY_STATES] as const;

export type LegacyBizyState = (typeof LEGACY_BIZY_STATES)[number];
export type BizyState = Bizy3State | LegacyBizyState;

const LEGACY_STATE_ALIASES: Record<LegacyBizyState, Bizy3State> = {
	thinking: 'thinkingLeft',
	disappointed: 'disappointedLeft',
	buzzing: 'flyingRight',
};

export const resolveBizyState = (state: BizyState | string): Bizy3State => {
	if (Object.prototype.hasOwnProperty.call(LEGACY_STATE_ALIASES, state)) {
		return LEGACY_STATE_ALIASES[state as LegacyBizyState];
	}
	return (ALL_BIZY3_STATES as readonly string[]).includes(state) ? (state as Bizy3State) : 'curious';
};

export type BizySize = 'sm' | 'md' | 'lg' | 'xl' | number;

export type WingFlutterMode = 'rotate' | 'scaleY' | 'squishStretch';

export type BizyAnimationOverrides = {
	/** Gentle vertical + rotation oscillation simulating hovering flight */
	hoverBob?: boolean;
	/** Subtle eye movement toward the cursor when it is nearby */
	eyeTracking?: boolean;
	/** Idle left-right eye sway while peeking; pauses when the cursor is nearby (default: true in peeking state) */
	peekEyeSway?: boolean;
	/** Periodic eye blink (scaleY squish every few seconds) */
	blink?: boolean;
	/** Ambient antenna sway with springy secondary motion */
	antennaSway?: boolean;
	/** @deprecated Bizy3 uses its Figma-authored squish/stretch flutter; retained for call-site compatibility. */
	wingMode?: WingFlutterMode;
	/** Wing flutter cycle duration in seconds — lower = faster flapping (default: 1.9) */
	wingSpeed?: number;
	/** Wing flutter intensity multiplier (default: 1) */
	wingAmplitude?: number;
};

export type BizyProps = {
	/** The emotional state of Bizy */
	state?: BizyState;
	/** Size of the component - 'sm' (48px), 'md' (80px), 'lg' (120px), or custom number */
	size?: BizySize;
	/** When true, all looping animations (wing flutter, star twinkle) are frozen */
	freeze?: boolean;
	/** Optional className for additional styling */
	className?: string;
	/** Experimental animation overrides for previewing new effects */
	animations?: BizyAnimationOverrides;
	/** Override arm stroke color (e.g. peeking arms on dark backgrounds). Antennae/stinger unchanged. */
	armStrokeColor?: string;
	/** Override arm stroke opacity when `armStrokeColor` is set. */
	armStrokeOpacity?: number;
	/** Skip the mount opacity fade-in so Bizy appears instantly (e.g. when remounted mid-flight). */
	skipEntranceFade?: boolean;
	/** Preserve the Figma fuzzy texture. Disable only for tiny, high-density surfaces. */
	texture?: boolean;
	/** Called when Bizy's painted body is clicked. */
	onBodyClick?: () => void;
};
